/**
 * © 2025 Little Shilling, Inc.
 * Shon Little
 * Created: 2025-12-15
 */

import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReauth } from './baseQuery';

/**
 * Store interface matching backend model
 */
export interface Store {
  id: string;
  name: string;
  notes: string;
  display_order: number;
  is_active: boolean;
  item_count: number;
  items_on_list_count: number;
  estimated_total: string;
  created_at: string;
  updated_at: string;
}

/**
 * Shopping Item interface matching backend model
 */
export interface ShoppingItem {
  id: string;
  name: string;
  location: string;
  price: string | null;
  quantity: number;
  is_on_list: boolean;
  is_checked_off: boolean;
  display_order: number;
  is_active: boolean;
  subtotal: string | null;
  created_at: string;
  updated_at: string;
}

/**
 * Create store request data
 */
export interface CreateStoreData {
  name: string;
  notes?: string;
  display_order?: number;
}

/**
 * Update store request data
 */
export interface UpdateStoreData {
  name?: string;
  notes?: string;
  display_order?: number;
  is_active?: boolean;
}

/**
 * Create shopping item request data
 */
export interface CreateShoppingItemData {
  store: string; // Store ID
  name: string;
  location?: string;
  price?: string;
  quantity?: number;
  is_on_list?: boolean;
}

/**
 * Update shopping item request data
 */
export interface UpdateShoppingItemData {
  name?: string;
  location?: string;
  price?: string | null;
  quantity?: number;
  is_on_list?: boolean;
  is_checked_off?: boolean;
  display_order?: number;
  is_active?: boolean;
}

/**
 * Bulk update request data
 */
export interface BulkUpdateData {
  item_updates: Array<{
    id: string;
    is_on_list?: boolean;
    is_checked_off?: boolean;
  }>;
}

/**
 * Shopping list response
 */
export interface ShoppingListResponse {
  store_id: string;
  store_name: string;
  total: string;
  item_count: number;
  unchecked_count: number;
  checked_count: number;
  items: ShoppingItem[];
}

/**
 * RTK Query API slice for Shopping List feature
 */
export const shoppingListApi = createApi({
  reducerPath: 'shoppingListApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['Stores', 'ShoppingItems', 'ShoppingList'],
  endpoints: builder => ({
    // ===== STORE ENDPOINTS =====

    /**
     * Get all stores for the authenticated user
     * GET /api/v1/shopping-list/stores/
     */
    getStores: builder.query<Store[], void>({
      query: () => '/shopping-list/stores/',
      transformResponse: (response: any) => {
        // With pagination disabled, response is directly an array
        if (Array.isArray(response)) {
          return response;
        }
        return [];
      },
      providesTags: result =>
        result
          ? [
              ...result.map(({ id }) => ({ type: 'Stores' as const, id })),
              { type: 'Stores', id: 'LIST' },
            ]
          : [{ type: 'Stores', id: 'LIST' }],
    }),

    /**
     * Get a specific store by ID
     * GET /api/v1/shopping-list/stores/{storeId}/
     */
    getStore: builder.query<Store, string>({
      query: storeId => `/shopping-list/stores/${storeId}/`,
      providesTags: (result, error, id) => [{ type: 'Stores', id }],
    }),

    /**
     * Create a new store
     * POST /api/v1/shopping-list/stores/
     */
    createStore: builder.mutation<Store, CreateStoreData>({
      query: data => ({
        url: '/shopping-list/stores/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: [{ type: 'Stores', id: 'LIST' }],
    }),

    /**
     * Update an existing store
     * PATCH /api/v1/shopping-list/stores/{storeId}/
     */
    updateStore: builder.mutation<
      Store,
      { storeId: string; data: UpdateStoreData }
    >({
      query: ({ storeId, data }) => ({
        url: `/shopping-list/stores/${storeId}/`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (result, error, { storeId }) => [
        { type: 'Stores', id: storeId },
        { type: 'Stores', id: 'LIST' },
      ],
    }),

    /**
     * Delete a store
     * DELETE /api/v1/shopping-list/stores/{storeId}/
     */
    deleteStore: builder.mutation<void, string>({
      query: storeId => ({
        url: `/shopping-list/stores/${storeId}/`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: 'Stores', id: 'LIST' }],
    }),

    /**
     * Duplicate a store and all its items
     * POST /api/v1/shopping-list/stores/{storeId}/duplicate/
     */
    duplicateStore: builder.mutation<Store, string>({
      query: storeId => ({
        url: `/shopping-list/stores/${storeId}/duplicate/`,
        method: 'POST',
      }),
      invalidatesTags: [{ type: 'Stores', id: 'LIST' }],
    }),

    // ===== SHOPPING ITEM ENDPOINTS =====

    /**
     * Get all items for a specific store
     * GET /api/v1/shopping-list/items/?store={storeId}
     */
    getItems: builder.query<ShoppingItem[], string>({
      query: storeId => ({
        url: '/shopping-list/items/',
        params: { store: storeId },
      }),
      transformResponse: (response: any) => {
        // With pagination disabled, response is directly an array
        if (Array.isArray(response)) {
          return response;
        }
        return [];
      },
      providesTags: (result, error, storeId) =>
        result
          ? [
              ...result.map(({ id }) => ({
                type: 'ShoppingItems' as const,
                id,
              })),
              { type: 'ShoppingItems', id: `STORE-${storeId}` },
            ]
          : [{ type: 'ShoppingItems', id: `STORE-${storeId}` }],
    }),

    /**
     * Create a new shopping item
     * POST /api/v1/shopping-list/items/
     */
    createItem: builder.mutation<ShoppingItem, CreateShoppingItemData>({
      query: data => ({
        url: '/shopping-list/items/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (result, error, { store }) => [
        { type: 'ShoppingItems', id: `STORE-${store}` },
        { type: 'Stores', id: store },
      ],
    }),

    /**
     * Update an existing shopping item
     * PATCH /api/v1/shopping-list/items/{itemId}/
     */
    updateItem: builder.mutation<
      ShoppingItem,
      { itemId: string; data: UpdateShoppingItemData }
    >({
      query: ({ itemId, data }) => ({
        url: `/shopping-list/items/${itemId}/`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (result, error, { itemId }) => [
        { type: 'ShoppingItems', id: itemId },
      ],
    }),

    /**
     * Delete a shopping item
     * DELETE /api/v1/shopping-list/items/{itemId}/
     */
    deleteItem: builder.mutation<void, { itemId: string; storeId: string }>({
      query: ({ itemId }) => ({
        url: `/shopping-list/items/${itemId}/`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { storeId }) => [
        { type: 'ShoppingItems', id: `STORE-${storeId}` },
        { type: 'Stores', id: storeId },
      ],
    }),

    /**
     * Bulk update item checkboxes
     * POST /api/v1/shopping-list/items/bulk_update/
     */
    bulkUpdateItems: builder.mutation<
      { updated_count: number; items: ShoppingItem[] },
      { data: BulkUpdateData; storeId: string }
    >({
      query: ({ data }) => ({
        url: '/shopping-list/items/bulk_update/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (result, error, { storeId }) => [
        { type: 'ShoppingItems', id: `STORE-${storeId}` },
      ],
    }),

    /**
     * Get shopping list view for a store
     * GET /api/v1/shopping-list/items/shopping_list/?store={storeId}&reverse={reverse}
     */
    getShoppingList: builder.query<
      ShoppingListResponse,
      { storeId: string; reverse?: boolean }
    >({
      query: ({ storeId, reverse = false }) => ({
        url: '/shopping-list/items/shopping_list/',
        params: {
          store: storeId,
          reverse: reverse ? 'true' : 'false',
        },
      }),
      providesTags: (result, error, { storeId }) => [
        { type: 'ShoppingList', id: storeId },
      ],
    }),
  }),
});

export const {
  useGetStoresQuery,
  useGetStoreQuery,
  useCreateStoreMutation,
  useUpdateStoreMutation,
  useDeleteStoreMutation,
  useDuplicateStoreMutation,
  useGetItemsQuery,
  useCreateItemMutation,
  useUpdateItemMutation,
  useDeleteItemMutation,
  useBulkUpdateItemsMutation,
  useGetShoppingListQuery,
} = shoppingListApi;
